import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import PartnerLayout from "./PartnerLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  UserPlus,
  Clock,
  TrendingUp,
  Repeat,
  Sparkles,
  Wallet,
  Star,
  ArrowLeft,
  ArrowRight,
  Play,
  Pause,
  Square,
  Trash2,
  Target,
  Gift,
  ArrowDownToLine,
  ArrowLeftRight,
  MessageSquare,
  ChevronRight,
  Loader2,
  Zap,
} from "lucide-react";
import type { KudozIncentiveTemplate, KudozIncentiveConfigField } from "@shared/kudozIncentiveTemplates";

interface KudozIncentive {
  id: string;
  restaurantId: string;
  eatzyBusinessId: string;
  templateType: string;
  name: string;
  status: string;
  kudozBudget: number | null;
  kudozSpent: number;
  startDate: string | null;
  endDate: string | null;
  templateConfig: Record<string, any>;
  stepsSnapshot: any[];
  metrics: Record<string, number>;
  createdAt: string;
  updatedAt: string;
}

const ICON_MAP: Record<string, typeof UserPlus> = {
  UserPlus, Clock, TrendingUp, Repeat, Sparkles, Wallet, Star,
};

const ACTION_ICONS: Record<string, typeof Gift> = {
  kudoz_give: Gift,
  kudoz_receive: ArrowDownToLine,
  kudoz_match: ArrowLeftRight,
  message: MessageSquare,
};

function getCategoryBadge(category: 'give' | 'receive' | 'both') {
  if (category === 'give') {
    return <Badge variant="outline" className="text-xs">You Give Kudoz</Badge>;
  }
  if (category === 'receive') {
    return <Badge variant="secondary" className="text-xs">You Receive Kudoz</Badge>;
  }
  return <Badge variant="outline" className="text-xs">Give & Receive</Badge>;
}

type View = 'dashboard' | 'select' | 'setup';

export default function PartnerKudozIncentives() {
  const { toast } = useToast();
  const restaurantId = localStorage.getItem('partnerRestaurantId') || '';
  const restaurantName = localStorage.getItem('partnerRestaurantName') || '';
  const eatzyToken = sessionStorage.getItem('partnerEatzyToken');
  const businessId = localStorage.getItem('partnerRestaurantProfile')
    ? (() => { try { return JSON.parse(localStorage.getItem('partnerRestaurantProfile')!).business_id; } catch { return ''; } })()
    : '';

  const [view, setView] = useState<View>('dashboard');
  const [selectedTemplate, setSelectedTemplate] = useState<KudozIncentiveTemplate | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, any>>({});
  const [incentiveName, setIncentiveName] = useState('');
  const [budgetEnabled, setBudgetEnabled] = useState(false);
  const [budgetAmount, setBudgetAmount] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; incentiveId: string; action: string }>({ open: false, incentiveId: '', action: '' });

  const { data: templates, isLoading: templatesLoading } = useQuery<KudozIncentiveTemplate[]>({
    queryKey: ['/api/partner/kudoz-incentives/templates'],
    queryFn: async () => {
      const res = await fetch('/api/partner/kudoz-incentives/templates', {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) throw new Error('Failed to fetch templates');
      return res.json();
    },
    enabled: !!eatzyToken,
  });

  const { data: incentivesData, isLoading: incentivesLoading } = useQuery<KudozIncentive[]>({
    queryKey: ['/api/partner/kudoz-incentives', restaurantId],
    queryFn: async () => {
      const res = await fetch(`/api/partner/kudoz-incentives/${restaurantId}`, {
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) throw new Error('Failed to fetch incentives');
      return res.json();
    },
    enabled: !!eatzyToken && !!restaurantId,
  });

  const createMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await fetch('/api/partner/kudoz-incentives', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken || '',
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create incentive');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/kudoz-incentives', restaurantId] });
      toast({ title: "Incentive activated", description: "Your Kudoz incentive is now active." });
      resetSetup();
      setView('dashboard');
    },
    onError: (err: any) => {
      toast({ title: "Failed to activate", description: err.message, variant: "destructive" });
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ incentiveId, status }: { incentiveId: string; status: string }) => {
      const res = await fetch(`/api/partner/kudoz-incentives/${incentiveId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-Eatzy-Token': eatzyToken || '',
        },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update incentive');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/kudoz-incentives', restaurantId] });
      toast({ title: "Incentive updated" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (incentiveId: string) => {
      const res = await fetch(`/api/partner/kudoz-incentives/${incentiveId}`, {
        method: 'DELETE',
        headers: { 'X-Eatzy-Token': eatzyToken || '' },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to delete incentive');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/partner/kudoz-incentives', restaurantId] });
      toast({ title: "Incentive deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  function resetSetup() {
    setSelectedTemplate(null);
    setConfigValues({});
    setIncentiveName('');
    setBudgetEnabled(false);
    setBudgetAmount('');
    setDurationDays('30');
  }

  function handleSelectTemplate(template: KudozIncentiveTemplate) {
    if (!template.available) return;
    setSelectedTemplate(template);
    const defaults: Record<string, any> = {};
    template.configFields.forEach(f => { defaults[f.key] = f.defaultValue; });
    setConfigValues(defaults);
    setIncentiveName(template.name);
    setView('setup');
  }

  function handleLaunch() {
    if (!selectedTemplate || !incentiveName.trim()) {
      toast({ title: "Missing info", description: "Please provide an incentive name.", variant: "destructive" });
      return;
    }

    const now = new Date();
    const endDate = new Date(now);
    endDate.setDate(endDate.getDate() + parseInt(durationDays));

    createMutation.mutate({
      restaurantId,
      eatzyBusinessId: businessId,
      templateType: selectedTemplate.type,
      name: incentiveName.trim(),
      kudozBudget: budgetEnabled && budgetAmount ? parseInt(budgetAmount) : null,
      startDate: now.toISOString(),
      endDate: endDate.toISOString(),
      templateConfig: configValues,
    });
  }

  function handleStatusAction(incentiveId: string, action: string) {
    setConfirmDialog({ open: true, incentiveId, action });
  }

  function confirmStatusAction() {
    const { incentiveId, action } = confirmDialog;
    if (action === 'delete') {
      deleteMutation.mutate(incentiveId);
    } else {
      statusMutation.mutate({ incentiveId, status: action });
    }
    setConfirmDialog({ open: false, incentiveId: '', action: '' });
  }

  function formatKudoz(amount: number) {
    return `${amount.toLocaleString()} Kudoz`;
  }

  function getStatusBadge(status: string) {
    const variants: Record<string, { variant: "default" | "secondary" | "outline" | "destructive"; label: string }> = {
      draft: { variant: 'secondary', label: 'Draft' },
      active: { variant: 'default', label: 'Active' },
      paused: { variant: 'outline', label: 'Paused' },
      completed: { variant: 'secondary', label: 'Completed' },
      expired: { variant: 'destructive', label: 'Expired' },
    };
    const v = variants[status] || variants.draft;
    return <Badge variant={v.variant}>{v.label}</Badge>;
  }

  const activeIncentives = incentivesData?.filter(c => c.status === 'active') || [];
  const otherIncentives = incentivesData?.filter(c => c.status !== 'active') || [];

  return (
    <PartnerLayout>
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6">
        {view === 'dashboard' && (
          <>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h1 className="text-2xl font-bold">Kudoz Incentives</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Kudoz-powered programs that reward your regulars and keep them coming back
                </p>
              </div>
              <Button onClick={() => setView('select')} data-testid="button-new-incentive">
                <Zap className="w-4 h-4 mr-2" />
                New Incentive
              </Button>
            </div>

            {incentivesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : !incentivesData || incentivesData.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <Target className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Kudoz incentives yet</h3>
                  <p className="text-sm text-muted-foreground mb-6 max-w-md">
                    Set up a Kudoz program to reward customers automatically — on their first order, their 5th visit, or any milestone you choose.
                  </p>
                  <Button onClick={() => setView('select')}>
                    <Zap className="w-4 h-4 mr-2" />
                    Create Incentive
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-6">
                {activeIncentives.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Play className="w-4 h-4 text-green-500" />
                      Active Incentives
                    </h2>
                    <div className="grid gap-4">
                      {activeIncentives.map(incentive => (
                        <IncentiveCard
                          key={incentive.id}
                          incentive={incentive}
                          templates={templates || []}
                          onStatusAction={handleStatusAction}
                          formatKudoz={formatKudoz}
                          getStatusBadge={getStatusBadge}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {otherIncentives.length > 0 && (
                  <div className="space-y-3">
                    <h2 className="text-lg font-semibold text-muted-foreground">
                      Past & Paused Incentives
                    </h2>
                    <div className="grid gap-4">
                      {otherIncentives.map(incentive => (
                        <IncentiveCard
                          key={incentive.id}
                          incentive={incentive}
                          templates={templates || []}
                          onStatusAction={handleStatusAction}
                          formatKudoz={formatKudoz}
                          getStatusBadge={getStatusBadge}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {view === 'select' && (
          <>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => setView('dashboard')} data-testid="button-back">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">Choose an Incentive Program</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Select the goal you want to achieve — we'll handle the rest
                </p>
              </div>
            </div>

            {templatesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {templates?.map(template => {
                  const Icon = ICON_MAP[template.icon] || Target;
                  return (
                    <Card
                      key={template.type}
                      className={`cursor-pointer transition-all ${
                        template.available
                          ? 'hover-elevate'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                      onClick={() => handleSelectTemplate(template)}
                      data-testid={`template-${template.type}`}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between gap-2">
                          <div
                            className="p-2 rounded-md"
                            style={{ backgroundColor: `${template.color}15` }}
                          >
                            <Icon className="w-5 h-5" style={{ color: template.color }} />
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getCategoryBadge(template.category)}
                            {!template.available && (
                              <Badge variant="outline" className="text-xs">Coming Soon</Badge>
                            )}
                          </div>
                        </div>
                        <CardTitle className="text-base mt-2">{template.name}</CardTitle>
                        <CardDescription className="text-xs">{template.tagline}</CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm text-muted-foreground">{template.description}</p>
                        {template.available && template.steps.length > 0 && (
                          <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{template.steps.length} automated steps</span>
                          </div>
                        )}
                      </CardContent>
                      {template.available && (
                        <CardFooter className="pt-0">
                          <Button variant="ghost" size="sm" className="ml-auto gap-1">
                            Set up <ChevronRight className="w-3 h-3" />
                          </Button>
                        </CardFooter>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </>
        )}

        {view === 'setup' && selectedTemplate && (
          <>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => { resetSetup(); setView('select'); }} data-testid="button-back-setup">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{selectedTemplate.name}</h1>
                <p className="text-sm text-muted-foreground mt-1">{selectedTemplate.tagline}</p>
              </div>
            </div>

            <div className="grid gap-6 lg:grid-cols-5">
              <div className="lg:col-span-3 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Incentive Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="incentiveName">Incentive name</Label>
                      <Input
                        id="incentiveName"
                        value={incentiveName}
                        onChange={e => setIncentiveName(e.target.value)}
                        placeholder="e.g., Welcome Bonus Program"
                        data-testid="input-incentive-name"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="duration">Duration</Label>
                      <Select value={durationDays} onValueChange={setDurationDays}>
                        <SelectTrigger data-testid="select-duration">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="7">7 days</SelectItem>
                          <SelectItem value="14">14 days</SelectItem>
                          <SelectItem value="30">30 days</SelectItem>
                          <SelectItem value="60">60 days</SelectItem>
                          <SelectItem value="90">90 days</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <Separator />

                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-0.5">
                        <Label>Set a Kudoz budget</Label>
                        <p className="text-xs text-muted-foreground">
                          Cap the total Kudoz awarded through this incentive
                        </p>
                      </div>
                      <Switch
                        checked={budgetEnabled}
                        onCheckedChange={setBudgetEnabled}
                        data-testid="toggle-budget"
                      />
                    </div>

                    {budgetEnabled && (
                      <div className="space-y-2">
                        <Label htmlFor="budget">Maximum Kudoz budget</Label>
                        <Input
                          id="budget"
                          type="number"
                          min="0"
                          step="100"
                          value={budgetAmount}
                          onChange={e => setBudgetAmount(e.target.value)}
                          placeholder="e.g., 5000"
                          data-testid="input-budget"
                        />
                      </div>
                    )}
                  </CardContent>
                </Card>

                {selectedTemplate.configFields.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Incentive Settings</CardTitle>
                      <CardDescription>Customize the incentive to match your needs</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {selectedTemplate.configFields.map(field => (
                        <ConfigFieldInput
                          key={field.key}
                          field={field}
                          value={configValues[field.key]}
                          onChange={(val) => setConfigValues(prev => ({ ...prev, [field.key]: val }))}
                        />
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="flex items-center justify-between gap-4">
                  <Button variant="outline" onClick={() => { resetSetup(); setView('select'); }}>
                    Back
                  </Button>
                  <Button
                    onClick={handleLaunch}
                    disabled={createMutation.isPending || !incentiveName.trim()}
                    data-testid="button-launch"
                  >
                    {createMutation.isPending ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    Activate Incentive
                  </Button>
                </div>
              </div>

              <div className="lg:col-span-2 space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <Zap className="w-4 h-4" />
                      Incentive Plan
                    </CardTitle>
                    <CardDescription>What happens when you activate</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-0">
                      {selectedTemplate.steps.map((step, i) => {
                        const ActionIcon = ACTION_ICONS[step.action.type] || MessageSquare;
                        return (
                          <div key={step.stepNumber} className="relative">
                            {i < selectedTemplate.steps.length - 1 && (
                              <div className="absolute left-[15px] top-[32px] bottom-0 w-px bg-border" />
                            )}
                            <div className="flex gap-3 pb-6">
                              <div
                                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                                style={{ backgroundColor: selectedTemplate.color }}
                              >
                                {step.stepNumber}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm">{step.name}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                                <div className="flex items-center gap-1.5 mt-2">
                                  <ActionIcon className="w-3 h-3 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">{step.action.label}</span>
                                </div>
                                <p className="text-xs text-muted-foreground mt-1 italic">{step.triggerDescription}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </>
        )}

        <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, incentiveId: '', action: '' })}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {confirmDialog.action === 'paused' && 'Pause Incentive'}
                {confirmDialog.action === 'active' && 'Resume Incentive'}
                {confirmDialog.action === 'completed' && 'Complete Incentive'}
                {confirmDialog.action === 'delete' && 'Delete Incentive'}
              </DialogTitle>
              <DialogDescription>
                {confirmDialog.action === 'paused' && 'Pausing will temporarily stop all automated actions. You can resume anytime.'}
                {confirmDialog.action === 'active' && 'This will resume all automated incentive actions.'}
                {confirmDialog.action === 'completed' && 'Completing this incentive will stop all actions permanently. This cannot be undone.'}
                {confirmDialog.action === 'delete' && 'This will permanently remove the incentive and all its data. This cannot be undone.'}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, incentiveId: '', action: '' })}>
                Cancel
              </Button>
              <Button
                variant={confirmDialog.action === 'delete' ? 'destructive' : 'default'}
                onClick={confirmStatusAction}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </PartnerLayout>
  );
}

function ConfigFieldInput({ field, value, onChange }: { field: KudozIncentiveConfigField; value: any; onChange: (val: any) => void }) {
  if (field.type === 'number') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <div className="relative">
          {field.unit === '$' && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
          )}
          <Input
            type="number"
            value={value ?? ''}
            onChange={e => onChange(parseFloat(e.target.value) || 0)}
            className={field.unit === '$' ? 'pl-9' : ''}
            data-testid={`input-config-${field.key}`}
          />
          {field.unit === '%' && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
          )}
        </div>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Select value={String(value)} onValueChange={onChange}>
          <SelectTrigger data-testid={`select-config-${field.key}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'multiselect') {
    const selectedValues: string[] = Array.isArray(value) ? value : [];
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <div className="flex flex-wrap gap-2">
          {field.options?.map(opt => {
            const isSelected = selectedValues.includes(opt.value);
            return (
              <Badge
                key={opt.value}
                variant={isSelected ? 'default' : 'outline'}
                className="cursor-pointer toggle-elevate"
                onClick={() => {
                  if (isSelected) {
                    onChange(selectedValues.filter(v => v !== opt.value));
                  } else {
                    onChange([...selectedValues, opt.value]);
                  }
                }}
                data-testid={`badge-option-${opt.value}`}
              >
                {opt.label}
              </Badge>
            );
          })}
        </div>
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  if (field.type === 'text') {
    return (
      <div className="space-y-2">
        <Label>{field.label}</Label>
        <Input
          value={value ?? ''}
          onChange={e => onChange(e.target.value)}
          placeholder={field.helpText || ''}
          data-testid={`input-config-${field.key}`}
        />
        {field.helpText && <p className="text-xs text-muted-foreground">{field.helpText}</p>}
      </div>
    );
  }

  return null;
}

function IncentiveCard({
  incentive,
  templates,
  onStatusAction,
  formatKudoz,
  getStatusBadge,
}: {
  incentive: KudozIncentive;
  templates: KudozIncentiveTemplate[];
  onStatusAction: (id: string, action: string) => void;
  formatKudoz: (amount: number) => string;
  getStatusBadge: (status: string) => JSX.Element;
}) {
  const template = templates.find(t => t.type === incentive.templateType);
  const Icon = template ? (ICON_MAP[template.icon] || Target) : Target;
  const color = template?.color || '#666';

  const budgetPercent = incentive.kudozBudget
    ? Math.min(100, Math.round((incentive.kudozSpent / incentive.kudozBudget) * 100))
    : null;

  const startDate = incentive.startDate ? new Date(incentive.startDate).toLocaleDateString() : '';
  const endDate = incentive.endDate ? new Date(incentive.endDate).toLocaleDateString() : '';
  const daysRemaining = incentive.endDate
    ? Math.max(0, Math.ceil((new Date(incentive.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return (
    <Card data-testid={`incentive-card-${incentive.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="flex-shrink-0 p-2 rounded-md"
              style={{ backgroundColor: `${color}15` }}
            >
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm">{incentive.name}</h3>
                {getStatusBadge(incentive.status)}
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {template?.name || incentive.templateType}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            {incentive.status === 'active' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(incentive.id, 'paused')}
                  title="Pause"
                >
                  <Pause className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(incentive.id, 'completed')}
                  title="Complete"
                >
                  <Square className="w-4 h-4" />
                </Button>
              </>
            )}
            {incentive.status === 'paused' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(incentive.id, 'active')}
                  title="Resume"
                >
                  <Play className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onStatusAction(incentive.id, 'delete')}
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
            {(incentive.status === 'completed' || incentive.status === 'expired' || incentive.status === 'draft') && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onStatusAction(incentive.id, 'delete')}
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
          <div>
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-sm font-medium">{startDate} – {endDate}</p>
            {daysRemaining !== null && incentive.status === 'active' && (
              <p className="text-xs text-muted-foreground">{daysRemaining} days left</p>
            )}
          </div>
          {incentive.kudozBudget && (
            <div>
              <p className="text-xs text-muted-foreground">Kudoz Budget</p>
              <p className="text-sm font-medium">
                {formatKudoz(incentive.kudozSpent)} / {formatKudoz(incentive.kudozBudget)}
              </p>
              <div className="mt-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${budgetPercent}%`,
                    backgroundColor: (budgetPercent || 0) > 80 ? '#ef4444' : color,
                  }}
                />
              </div>
            </div>
          )}
          <div>
            <p className="text-xs text-muted-foreground">Customers Reached</p>
            <p className="text-sm font-medium">{(incentive.metrics as any)?.customersReached || 0}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Kudoz Awarded</p>
            <p className="text-sm font-medium">
              {(incentive.metrics as any)?.kudozAwarded || 0}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
